import { uid } from './ids.ts';
import { PROJECT_SECTIONS, type ColorName, type FileMeta, type Person } from './model.ts';
import {
  abilitiesCollection,
  assetsCollection,
  COLLECTION_COLORS,
  ideasCollection,
  itemsCollection,
  mechanicsCollection,
  opt,
  scratchCollection,
  tasksCollection,
  unitsCollection,
} from './presets.ts';
import type { NewCollection, ProjectDoc } from './project-doc.ts';
import { rich, type RichNode } from './rich.ts';
import { FACTION_PALETTES, fireballWav, heatMeterFile, paletteSheetFile, portraitFile, type GeneratedFile, type GlyphName, type Palette } from './sample-art.ts';

/**
 * What a new project is filled with: a copy of the Emberfall sample, or a blank project. Every
 * game is different, so a blank one starts with the game's own collections (made from scratch)
 * rather than a genre's.
 */
export type TemplateId = 'blank' | 'sample';

/** One of the things a game is made of (units, cards, levels…), made from scratch. */
export interface StarterCollection {
  name: string;
  icon?: string;
  color?: ColorName;
}

/**
 * The ready-made parts a blank project can start with, in sidebar order: Mechanics (design docs,
 * with a "Start here" guide), Ideas, Tasks, Assets, Boards and Votes.
 */
export const STARTER_SECTIONS = ['mechanics', 'ideas', 'tasks', 'assets', 'boards', 'votes'] as const;
export type StarterSection = (typeof STARTER_SECTIONS)[number];

export interface TemplateContext {
  /** Persist a generated file and return its metadata (hashing happens in the app). */
  putFile?: (file: GeneratedFile) => Promise<FileMeta>;
  /** The creating user. */
  me?: Person;
  now?: number;
  /**
   * A blank project's own collections, in sidebar order, ahead of the ready-made ones. Each
   * starts empty, with a table. Blank names are skipped.
   */
  collections?: StarterCollection[];
  /** Which ready-made parts a blank project starts with. All of them when left out. */
  sections?: readonly StarterSection[];
}

const DAY = 86_400_000;
const isoDate = (t: number) => new Date(t).toISOString().slice(0, 10);

/** The "Start here" guide. It only points at the parts the project starts with. */
function welcomeDoc(has: (section: StarterSection) => boolean): RichNode[] {
  const { h, p, b, ul, todo, callout, code } = rich;
  const parts: (string | RichNode)[][] = [
    ['a ', b('codex'), ' of units, items and anything else with real stats'],
    [b('design docs'), ' like this one'],
  ];
  if (has('ideas')) parts.push([b('ideas'), ' your team can vote on']);
  if (has('tasks')) parts.push([b('tasks')]);
  if (has('assets')) parts.push(['your ', b('assets')]);
  const list = parts.flatMap((part, i) => (i === 0 ? part : [i === parts.length - 1 ? '; and ' : '; ', ...part]));
  const voteOnIdeas: [boolean, ...(string | RichNode)[]][] = has('ideas') ? [[false, 'Open the Ideas board and vote on something.']] : [];
  return [
    p('Tomo keeps every part of your game in one place: ', ...list, '.'),
    h(2, 'A few things to try'),
    todo(
      [false, 'Type ', code('/'), ' on an empty line for headings, checklists, tables, callouts and more.'],
      [false, 'Type ', code('@'), ' to link any record — it shows up in that record’s backlinks.'],
      ...voteOnIdeas,
      [false, 'Add a Formula field to a codex collection, e.g. ', code('{Damage} * {Attack speed}'), '.'],
      [false, 'Press ', code('Ctrl K'), ' to jump anywhere.'],
    ),
    callout('idea', p('Everything is saved on this device and works offline. Upgrade a workspace to Pro when you want real-time collaboration, sync across devices and teammates.')),
    h(2, 'Suggested structure'),
    ul(
      'Codex — one collection per kind of thing in your game (units, cards, levels…).',
      'Mechanics — how systems work and why.',
      ...(has('ideas') ? [has('tasks') ? 'Ideas — the parking lot. Promote winners to tasks.' : 'Ideas — the parking lot.'] : []),
      ...(has('tasks') ? ['Tasks — production work, linked to what it implements.'] : []),
    ),
  ];
}

/** Fill a freshly initialised project. */
export async function applyTemplate(p: ProjectDoc, id: TemplateId, ctx: TemplateContext = {}): Promise<void> {
  if (id === 'sample') await applySample(p, ctx);
  else applyBlank(p, ctx);
  // Starting content is not something users should be able to "undo" away.
  p.undoManager.clear();
}

function applyBlank(p: ProjectDoc, ctx: TemplateContext) {
  const own = (ctx.collections ?? []).filter((c) => c.name.trim());
  const has = (section: StarterSection) => !ctx.sections || ctx.sections.includes(section);
  p.transact(() => {
    // Colors go round like the New collection dialog's, unless one was picked.
    own.forEach((c, i) => p.createCollection(scratchCollection(c.name, { icon: c.icon, color: c.color ?? COLLECTION_COLORS[i % COLLECTION_COLORS.length]! })));
    const mechanics = has('mechanics') ? p.createCollection(mechanicsCollection('Mechanics')) : null;
    if (has('ideas')) p.createCollection(ideasCollection());
    if (has('tasks')) p.createCollection(tasksCollection());
    if (has('assets')) p.createCollection(assetsCollection());
    // The guide is a design doc, so it comes with Mechanics.
    if (mechanics) p.createRecord(mechanics, { title: 'Start here', icon: 'i:hand', body: welcomeDoc(has), createdBy: ctx.me?.id ?? null, createdAt: ctx.now ?? Date.now() });
    if (has('boards')) p.createBoard({ name: 'Scratch board', description: 'Notes, sketches and reference art. Cluster them, then vote.' });
    // Sections left out stay off until they're added from the sidebar.
    for (const section of PROJECT_SECTIONS) p.setSection(section, has(section));
  });
}

/* ================================================================================================
 * Sample project: "Emberfall", a fantasy RTS with three factions
 * ============================================================================================== */

/** The sample's generated files (deterministic), keyed by asset. */
function sampleFiles(): Record<string, GeneratedFile> {
  const P = FACTION_PALETTES;
  const portrait = (name: string, glyph: GlyphName, pal: Palette, seed: number, pips: number) => portraitFile(`${name}.svg`, glyph, pal, seed, pips);
  return {
    emberWarden: portrait('ember-warden', 'shield', P.ember, 11, 1),
    cinderArcher: portrait('cinder-archer', 'bow', P.ember, 12, 1),
    pyromancer: portrait('pyromancer', 'flame', P.ember, 13, 2),
    magmaGolem: portrait('magma-golem', 'golem', P.ember, 14, 3),
    frostSentinel: portrait('frost-sentinel', 'shield', P.frost, 21, 2),
    rimeblade: portrait('rimeblade', 'sword', P.frost, 22, 4),
    glacierMystic: portrait('glacier-mystic', 'snow', P.frost, 23, 2),
    thornback: portrait('thornback', 'leaf', P.verdant, 31, 2),
    groveTender: portrait('grove-tender', 'staff', P.verdant, 32, 1),
    ancientOak: portrait('ancient-oak', 'tree', P.verdant, 33, 3),
    frostPalette: paletteSheetFile('frostbound-palette.svg', 'Frostbound — color script', [
      { hex: '#07162E', label: 'Abyss' },
      { hex: '#2563A8', label: 'Glacier' },
      { hex: '#5FB3E6', label: 'Rime' },
      { hex: '#B9E6FF', label: 'Frost' },
      { hex: '#EAF8FF', label: 'Snowlight' },
    ]),
    heatMeter: heatMeterFile('heat-meter-mockup.svg'),
    fireballSfx: fireballWav('fireball-impact.wav'),
  };
}

type SampleMetas = Record<string, FileMeta | null>;

async function applySample(p: ProjectDoc, ctx: TemplateContext) {
  // Store files first (async), then write the whole document in ONE transaction so it lands in
  // local storage as a single update (no half-created project if the tab closes right away).
  const files = sampleFiles();
  const metas: SampleMetas = {};
  for (const [key, file] of Object.entries(files)) metas[key] = ctx.putFile ? await ctx.putFile(file) : null;
  p.transact(() => writeSample(p, ctx, metas));
}

function writeSample(p: ProjectDoc, ctx: TemplateContext, F: SampleMetas) {
  const t0 = ctx.now ?? Date.now();
  const { h, p: para, b, i, mention, ul, ol, todo, callout, table, quote, hl } = rich;

  const ana: Person = { id: 'sample-ana', name: 'Ana Rivera', color: '#F97066', sample: true };
  const kenji: Person = { id: 'sample-kenji', name: 'Kenji Watanabe', color: '#60A5FA', sample: true };
  const maya: Person = { id: 'sample-maya', name: 'Maya Okafor', color: '#34D399', sample: true };
  for (const person of [ana, kenji, maya]) p.upsertPerson(person);
  if (ctx.me) p.upsertPerson(ctx.me);

  /* ---------------------------------------------------------------- collections */
  const C = { assets: uid(), abilities: uid(), units: uid(), items: uid(), mechanics: uid(), lore: uid(), ideas: uid(), tasks: uid() };
  const factions = { ember: opt('Ember Legion', 'orange'), frost: opt('Frostbound', 'blue'), verdant: opt('Verdant Circle', 'green') };
  const unitsDef = { ...unitsCollection({ assets: C.assets, abilities: C.abilities }, Object.values(factions)), id: C.units };
  const abilitiesDef = { ...abilitiesCollection({ assets: C.assets }), id: C.abilities };
  const itemsDef = { ...itemsCollection({ assets: C.assets }), id: C.items, name: 'Hero items' };
  const mechanicsDef = { ...mechanicsCollection('Mechanics'), id: C.mechanics };
  const loreDef = { ...mechanicsCollection('Lore'), id: C.lore, icon: 'i:book-open', color: 'brown' as const, titleLabel: 'Entry', description: 'The world of Emberfall.' };
  const ideasDef = { ...ideasCollection(), id: C.ideas };
  const tasksDef = { ...tasksCollection(), id: C.tasks };
  const assetsDef = { ...assetsCollection(), id: C.assets };
  for (const c of [unitsDef, abilitiesDef, itemsDef, mechanicsDef, loreDef, ideasDef, tasksDef, assetsDef]) p.createCollection(c);

  const fid = (def: NewCollection, name: string) => def.fields!.find((x) => x.name === name)!.id!;
  const optId = (def: NewCollection, field: string, label: string) =>
    def.fields!.find((x) => x.name === field)!.config!.options!.find((o) => o.label === label)!.id;

  /* ---------------------------------------------------------------- record ids (for cross links) */
  const R = {
    emberWarden: uid(), cinderArcher: uid(), pyromancer: uid(), magmaGolem: uid(),
    frostSentinel: uid(), rimeblade: uid(), glacierMystic: uid(),
    thornback: uid(), groveTender: uid(), ancientOak: uid(),
    fireball: uid(), flamingVolley: uid(), emberShield: uid(), moltenCore: uid(), frostNova: uid(), blizzard: uid(), glacialWard: uid(), roots: uid(), rejuvenate: uid(),
    combat: uid(), economy: uid(), heat: uid(), fog: uid(), sundering: uid(), factionsDoc: uid(),
    ideaWeather: uid(), ideaAltars: uid(), ideaDayNight: uid(), ideaStags: uid(), ideaCoop: uid(), ideaMusic: uid(), ideaReplay: uid(), ideaBurning: uid(),
  };

  /* ---------------------------------------------------------------- assets (files were stored up front) */
  const A = assetsDef;
  const assetRecord = (title: string, meta: FileMeta | null, status: string, tags: string[], artist: Person | null, usedIn: string[], daysAgo: number, body: RichNode[] = []) => {
    const values: Record<string, unknown> = {
      [fid(A, 'Status')]: optId(A, 'Status', status),
      [fid(A, 'Tags')]: tags.map((t) => optId(A, 'Tags', t)),
      [fid(A, 'Used in')]: usedIn,
    };
    // A picture is the record's cover; anything else (the SFX, the palette sheet) goes in Files.
    const image = meta?.mime.startsWith('image/') ? meta : null;
    if (meta && !image) values[fid(A, 'Files')] = [meta];
    if (artist) values[fid(A, 'Artist')] = [artist.id];
    return p.createRecord(C.assets, { title, values, cover: image, createdBy: artist?.id ?? kenji.id, createdAt: t0 - daysAgo * DAY, body });
  };

  const portraits = {
    emberWarden: assetRecord('Portrait — Ember Warden', F.emberWarden, 'Final', ['Character'], kenji, [R.emberWarden], 20),
    cinderArcher: assetRecord('Portrait — Cinder Archer', F.cinderArcher, 'Final', ['Character'], kenji, [R.cinderArcher], 19),
    pyromancer: assetRecord('Portrait — Pyromancer', F.pyromancer, 'Final', ['Character'], kenji, [R.pyromancer], 18),
    magmaGolem: assetRecord('Portrait — Magma Golem', F.magmaGolem, 'Review', ['Character'], kenji, [R.magmaGolem], 9),
    frostSentinel: assetRecord('Portrait — Frost Sentinel', F.frostSentinel, 'Final', ['Character'], kenji, [R.frostSentinel], 17),
    rimeblade: assetRecord('Portrait — Rimeblade', F.rimeblade, 'Review', ['Character'], kenji, [R.rimeblade], 3),
    glacierMystic: assetRecord('Portrait — Glacier Mystic', F.glacierMystic, 'WIP', ['Character'], kenji, [R.glacierMystic], 2),
    thornback: assetRecord('Portrait — Thornback', F.thornback, 'Final', ['Character'], kenji, [R.thornback], 16),
    groveTender: assetRecord('Portrait — Grove Tender', F.groveTender, 'Final', ['Character'], kenji, [R.groveTender], 15),
    ancientOak: assetRecord('Portrait — Ancient Oak', F.ancientOak, 'WIP', ['Character'], kenji, [R.ancientOak], 1),
  };
  const frostPalette = assetRecord('Frostbound color script', F.frostPalette, 'Concept', ['Concept'], kenji, [], 6, [para('First pass. Waiting on the art-direction vote before committing.')]);
  const heatMeter = assetRecord('Heat meter — UI mockup', F.heatMeter, 'Review', ['UI'], ana, [R.heat], 4);
  const fireballSfx = assetRecord('SFX — Fireball impact', F.fireballSfx, 'WIP', ['SFX'], kenji, [R.fireball], 2, [para('Whoosh + low boom. Needs a brighter crackle layer on impact.')]);

  /* ---------------------------------------------------------------- abilities */
  const AB = abilitiesDef;
  const ability = (id: string, title: string, type: string, element: string, dmg: [number, number] | null, cd: number, mana: number | null, radius: number | null, target: string, effect: string, daysAgo: number, body: RichNode[] = []) =>
    p.createRecord(C.abilities, {
      id,
      title,
      createdBy: ana.id,
      createdAt: t0 - daysAgo * DAY,
      values: {
        [fid(AB, 'Type')]: optId(AB, 'Type', type),
        [fid(AB, 'Element')]: optId(AB, 'Element', element),
        [fid(AB, 'Damage')]: dmg ?? undefined,
        [fid(AB, 'Cooldown')]: cd,
        [fid(AB, 'Mana')]: mana ?? undefined,
        [fid(AB, 'Radius')]: radius ?? undefined,
        [fid(AB, 'Target')]: optId(AB, 'Target', target),
        [fid(AB, 'Effect')]: effect,
      },
      body,
    });
  ability(R.fireball, 'Fireball', 'Active', 'Fire', [60, 80], 8, 40, 2, 'Area', 'Hurls a fireball that explodes on impact.', 21, [
    para('Main damage spell of the ', mention(R.pyromancer, 'Pyromancer'), '. Generates ', b('+6 Heat'), ' per enemy hit (see ', mention(R.heat, 'Heat'), ').'),
    callout('warning', para('Currently one-shots ', mention(R.groveTender, 'Grove Tender'), ' groups at 3 casters. Watch in playtest #3.')),
  ]);
  ability(R.flamingVolley, 'Flaming Volley', 'Active', 'Fire', [20, 30], 12, 30, 3, 'Area', 'Rains burning arrows over an area for 3 s.', 21);
  ability(R.emberShield, 'Ember Shield', 'Active', 'Fire', null, 20, 25, null, 'Self', 'Absorbs 150 damage; attackers take 10 fire damage.', 20);
  ability(R.moltenCore, 'Molten Core', 'Passive', 'Fire', [100, 140], 0, null, 3, 'Area', 'Explodes on death.', 12);
  ability(R.frostNova, 'Frost Nova', 'Active', 'Frost', [30, 40], 14, 50, 3, 'Area', 'Damages and slows nearby enemies by 40% for 3 s.', 19);
  ability(R.blizzard, 'Blizzard', 'Ultimate', 'Frost', [150, 220], 90, 120, 5, 'Area', 'A storm that deals damage over 6 s and slows 60%.', 8);
  ability(R.glacialWard, 'Glacial Ward', 'Active', 'Frost', null, 18, 35, null, 'Single', 'Shields an ally for 200 damage.', 14);
  ability(R.roots, 'Entangling Roots', 'Active', 'Nature', [10, 15], 16, 45, null, 'Single', 'Roots the target for 3 s.', 18);
  ability(R.rejuvenate, 'Rejuvenate', 'Active', 'Nature', null, 10, 30, null, 'Single', 'Heals 120 HP over 6 s.', 18);

  /* ---------------------------------------------------------------- units */
  const U = unitsDef;
  const unit = (
    id: string,
    title: string,
    faction: keyof typeof factions,
    role: string,
    tier: string,
    s: { hp: number; dmg: number; as: number; armor: number; move: number; range: number; cost: number; build: number },
    abilities: string[],
    portraitId: string,
    daysAgo: number,
    body: RichNode[] = [],
  ) =>
    p.createRecord(C.units, {
      id,
      title,
      createdBy: ana.id,
      createdAt: t0 - daysAgo * DAY,
      values: {
        [fid(U, 'Faction')]: factions[faction].id,
        [fid(U, 'Role')]: optId(U, 'Role', role),
        [fid(U, 'Tier')]: optId(U, 'Tier', tier),
        [fid(U, 'HP')]: s.hp,
        [fid(U, 'Damage')]: s.dmg,
        [fid(U, 'Attack speed')]: s.as,
        [fid(U, 'Armor')]: s.armor,
        [fid(U, 'Move speed')]: s.move,
        [fid(U, 'Range')]: s.range,
        [fid(U, 'Gold cost')]: s.cost,
        [fid(U, 'Build time')]: s.build,
        [fid(U, 'Abilities')]: abilities,
        [fid(U, 'Portrait')]: [portraitId],
      },
      body,
    });
  unit(R.emberWarden, 'Ember Warden', 'ember', 'Melee', 'T1', { hp: 620, dmg: 28, as: 1.1, armor: 4, move: 3, range: 1, cost: 120, build: 18 }, [R.emberShield], portraits.emberWarden, 22, [
    para('Front-line infantry of the Ember Legion. Cheap, sturdy, and the first unit most players build.'),
    h(3, 'Design intent'),
    ul('Soak damage while casters build Heat.', 'Should lose 1v1 to a Frost Sentinel, but win on cost.'),
  ]);
  unit(R.cinderArcher, 'Cinder Archer', 'ember', 'Ranged', 'T1', { hp: 380, dmg: 22, as: 1.4, armor: 1, move: 3.2, range: 6, cost: 110, build: 16 }, [R.flamingVolley], portraits.cinderArcher, 22);
  unit(R.pyromancer, 'Pyromancer', 'ember', 'Caster', 'T2', { hp: 420, dmg: 34, as: 0.9, armor: 1, move: 3, range: 5, cost: 190, build: 24 }, [R.fireball], portraits.pyromancer, 21, [
    para('Glass cannon. Uses ', mention(R.fireball, 'Fireball'), ' to build Heat quickly.'),
  ]);
  unit(R.magmaGolem, 'Magma Golem', 'ember', 'Siege', 'T3', { hp: 1800, dmg: 90, as: 0.5, armor: 8, move: 2, range: 1, cost: 420, build: 45 }, [R.moltenCore], portraits.magmaGolem, 12);
  unit(R.frostSentinel, 'Frost Sentinel', 'frost', 'Melee', 'T2', { hp: 900, dmg: 32, as: 0.9, armor: 7, move: 2.6, range: 1, cost: 200, build: 26 }, [R.frostNova], portraits.frostSentinel, 20);
  unit(R.rimeblade, 'Rimeblade', 'frost', 'Hero', 'Hero', { hp: 1400, dmg: 64, as: 1.2, armor: 6, move: 3.4, range: 1, cost: 600, build: 60 }, [R.blizzard, R.frostNova], portraits.rimeblade, 10, [
    para('Frostbound hero. Duelist that controls fights with ', mention(R.blizzard, 'Blizzard'), '.'),
    quote(para(i('“The cold does not hate you. It simply outlasts you.”'))),
  ]);
  unit(R.glacierMystic, 'Glacier Mystic', 'frost', 'Support', 'T2', { hp: 450, dmg: 18, as: 1, armor: 2, move: 3, range: 5, cost: 170, build: 22 }, [R.glacialWard], portraits.glacierMystic, 14);
  unit(R.thornback, 'Thornback', 'verdant', 'Melee', 'T2', { hp: 1000, dmg: 26, as: 1, armor: 9, move: 2.4, range: 1, cost: 210, build: 28 }, [R.roots], portraits.thornback, 18);
  unit(R.groveTender, 'Grove Tender', 'verdant', 'Support', 'T1', { hp: 400, dmg: 14, as: 1, armor: 2, move: 3.1, range: 4, cost: 100, build: 15 }, [R.rejuvenate], portraits.groveTender, 18);
  unit(R.ancientOak, 'Ancient Oak', 'verdant', 'Siege', 'T3', { hp: 2200, dmg: 70, as: 0.6, armor: 10, move: 1.6, range: 7, cost: 480, build: 50 }, [R.roots], portraits.ancientOak, 6);

  /* ---------------------------------------------------------------- hero items */
  const IT = itemsDef;
  const item = (title: string, rarity: string, slot: string, value: number, dice: string | null, weight: number, effect: string, stackable: boolean, icon: string) =>
    p.createRecord(C.items, {
      title,
      icon,
      createdBy: ana.id,
      createdAt: t0 - 9 * DAY,
      values: {
        [fid(IT, 'Rarity')]: optId(IT, 'Rarity', rarity),
        [fid(IT, 'Slot')]: optId(IT, 'Slot', slot),
        [fid(IT, 'Value')]: value,
        [fid(IT, 'Damage')]: dice ?? undefined,
        [fid(IT, 'Weight')]: weight,
        [fid(IT, 'Effect')]: effect,
        [fid(IT, 'Stackable')]: stackable || undefined,
      },
    });
  item('Cinderbrand', 'Legendary', 'Weapon', 2400, '2d8+6', 3.5, 'Attacks ignite the target for 3 s.', false, 'i:sword');
  item('Frostglass Aegis', 'Epic', 'Armor', 1600, null, 6, 'Blocks the first hit every 10 s.', false, 'i:shield');
  item('Verdant Charm', 'Rare', 'Trinket', 600, null, 0.2, '+2 HP regeneration.', false, 'i:clover');
  item("Traveler's Boots", 'Uncommon', 'Trinket', 250, null, 1.2, '+0.4 move speed.', false, 'i:footprints');
  item('Ember Salts', 'Common', 'Consumable', 40, null, 0.1, 'Restores 150 HP over 5 s.', true, 'i:flask-round');
  item('Rimeglass Shard', 'Rare', 'Material', 180, null, 0.3, 'Crafting material for Frostbound gear.', true, 'i:gem');

  /* ---------------------------------------------------------------- mechanics & lore */
  const M = mechanicsDef;
  const doc = (collection: string, def: NewCollection, id: string, title: string, icon: string, status: string, owner: Person, tags: string[], daysAgo: number, body: RichNode[]) =>
    p.createRecord(collection, {
      id,
      title,
      icon,
      createdBy: owner.id,
      createdAt: t0 - daysAgo * DAY,
      values: {
        [fid(def, 'Status')]: optId(def, 'Status', status),
        [fid(def, 'Owner')]: [owner.id],
        [fid(def, 'Tags')]: tags.map((t) => optId(def, 'Tags', t)),
      },
      body,
    });

  doc(C.mechanics, M, R.combat, 'Combat & damage formula', 'i:swords', 'Approved', ana, ['Core', 'Combat'], 25, [
    para('Every attack resolves in three steps. The goal is ', b('readable'), ' outcomes: a player should be able to guess who wins a fight by looking at the units.'),
    h(2, 'Resolution'),
    ol('Roll raw damage (abilities use their damage range).', 'Apply armor reduction.', 'Apply modifiers: Heat, auras, elemental weakness.'),
    h(2, 'Armor'),
    para('Armor reduces damage with diminishing returns:'),
    rich.codeBlock('damage_taken = raw_damage * 100 / (100 + armor * 6)', 'js'),
    table(['Armor', 'Reduction', 'Example unit'], ['0', '0%', 'Cinder Archer'], ['4', '19%', 'Ember Warden'], ['7', '30%', 'Frost Sentinel'], ['10', '38%', 'Ancient Oak']),
    callout('idea', para('Elemental weakness is being discussed in ', mention(R.ideaWeather, 'Weather affects elemental damage'), '.')),
    h(2, 'Open questions'),
    todo([true, 'Prototype in the combat sandbox'], [false, 'Tune the armor constant after playtest #3'], [false, 'Decide whether ', mention(R.magmaGolem, 'Magma Golem'), ' ignores armor on buildings']),
  ]);
  doc(C.mechanics, M, R.economy, 'Economy: gold & upkeep', 'i:coins', 'In review', maya, ['Economy'], 16, [
    para('Gold comes from mines (8 g/s each, max 5 workers) and from destroying enemy structures.'),
    h(2, 'Upkeep'),
    para('Army size taxes income so that turtling into a huge army has a cost:'),
    table(['Population', 'Income penalty'], ['0 – 40', '0%'], ['41 – 70', '30%'], ['71 – 100', '60%']),
    para('An ', mention(R.emberWarden, 'Ember Warden'), ' at 120 g is the baseline "1 supply" unit.'),
    callout('note', para('Tooltip copy for upkeep is done; numbers still need a balancing pass.')),
  ]);
  doc(C.mechanics, M, R.heat, 'Heat — Ember Legion faction mechanic', 'i:flame', 'Draft', ana, ['Combat'], 7, [
    para('Ember Legion units share a ', b('Heat'), ' meter that fills as they deal damage and drains out of combat.'),
    ul('+1 Heat per basic attack hit, +6 per ' + 'spell hit.', '−4 Heat per second out of combat.', 'At 100 Heat the army ' + 'Overheats for 8 s.'),
    h(3, 'Overheat'),
    para('+25% attack speed, but units take ', hl('2% max HP per second'), '. Casters like the ', mention(R.pyromancer, 'Pyromancer'), ' and spells like ', mention(R.fireball, 'Fireball'), ' are the main Heat builders.'),
    callout('warning', para('Overheat can snowball — watch win rates in playtests.')),
  ]);
  doc(C.mechanics, M, R.fog, 'Fog of war & vision', 'i:telescope', 'Draft', maya, ['Core', 'Tech'], 5, [
    para('Classic three-state fog: unexplored, explored (terrain only), visible.'),
    ul('Vision radius per unit (default 8).', 'High ground blocks vision from below.', 'Revealed by abilities for 2 s after casting.'),
  ]);
  const L = loreDef;
  doc(C.lore, L, R.sundering, 'The Sundering', 'i:mountain', 'Approved', kenji, ['Core'], 30, [
    para('A thousand years ago the world-flame beneath the mountain of Emberfall cracked. Fire, frost and the green wilds each claimed a shard.'),
    para('The ', b('Ember Legion'), ' guard the burning heart, the ', b('Frostbound'), ' seek to smother it forever, and the ', b('Verdant Circle'), ' believe the world must heal before anyone can rule it.'),
  ]);
  doc(C.lore, L, R.factionsDoc, 'Factions of Emberfall', 'i:flag', 'Draft', kenji, [], 12, [
    h(3, 'Ember Legion'),
    para('Aggressive, snowbally. Heroes: ', mention(R.pyromancer, 'Pyromancer'), '.'),
    h(3, 'Frostbound'),
    para('Control and attrition. Heroes: ', mention(R.rimeblade, 'Rimeblade'), '.'),
    h(3, 'Verdant Circle'),
    para('Sustain and territory. Siege: ', mention(R.ancientOak, 'Ancient Oak'), '.'),
  ]);

  /* ---------------------------------------------------------------- ideas */
  const I = ideasDef;
  const idea = (id: string, title: string, status: string, voters: Person[], area: string[], impact: number, effort: string, author: Person, daysAgo: number, body: RichNode[] = []) =>
    p.createRecord(C.ideas, {
      id,
      title,
      createdBy: author.id,
      createdAt: t0 - daysAgo * DAY,
      votes: Object.fromEntries(voters.map((v) => [v.id, 1])),
      values: {
        [fid(I, 'Status')]: optId(I, 'Status', status),
        [fid(I, 'Area')]: area.map((a) => optId(I, 'Area', a)),
        [fid(I, 'Impact')]: impact,
        [fid(I, 'Effort')]: optId(I, 'Effort', effort),
      },
      body,
    });
  idea(R.ideaWeather, 'Weather affects elemental damage', 'Discussing', [ana, kenji, maya], ['Gameplay'], 4, 'M', maya, 9, [
    para('Rain: −20% fire damage, +10% frost. Snow: frost units +0.3 move speed. Heat waves: Ember units build Heat 25% faster.'),
    para('Would make map choice matter more. Risk: randomness in ranked.'),
  ]);
  idea(R.ideaAltars, 'Hero respawn altars', 'Accepted', [ana, maya], ['Gameplay'], 4, 'M', ana, 14);
  idea(R.ideaDayNight, 'Day/night cycle with vision changes', 'Discussing', [kenji, maya], ['Gameplay', 'Tech'], 3, 'L', kenji, 8);
  idea(R.ideaStags, 'Stag riders for the Verdant Circle', 'New', [kenji], ['Gameplay', 'Art'], 3, 'L', kenji, 3);
  idea(R.ideaCoop, 'Co-op campaign mode', 'Parked', [ana, kenji, maya], ['Gameplay', 'Tech'], 5, 'XL', maya, 26, [para('Parked until after the vertical slice.')]);
  idea(R.ideaMusic, 'Dynamic music layers per faction', 'New', [kenji, ana], ['Audio'], 3, 'M', kenji, 2);
  idea(R.ideaReplay, 'Replay theater with timeline scrubbing', 'New', [maya], ['Tech', 'UX'], 2, 'L', maya, 1);
  idea(R.ideaBurning, 'Ember units leave burning ground', 'Rejected', [], ['Gameplay'], 2, 'S', ana, 20, [para('Too much visual noise in big fights; Heat already covers the fantasy.')]);

  /* ---------------------------------------------------------------- tasks */
  const T = tasksDef;
  const task = (title: string, status: string, priority: string, who: Person, dueInDays: number | null, labels: string[], estimate: number | null, related: string[], daysAgo: number) =>
    p.createRecord(C.tasks, {
      title,
      createdBy: ana.id,
      createdAt: t0 - daysAgo * DAY,
      values: {
        [fid(T, 'Status')]: optId(T, 'Status', status),
        [fid(T, 'Priority')]: optId(T, 'Priority', priority),
        [fid(T, 'Assignee')]: [who.id],
        [fid(T, 'Due')]: dueInDays === null ? undefined : isoDate(t0 + dueInDays * DAY),
        [fid(T, 'Labels')]: labels.map((l) => optId(T, 'Labels', l)),
        [fid(T, 'Estimate')]: estimate ?? undefined,
        [fid(T, 'Related')]: related,
      },
    });
  task('Implement damage formula v2', 'In progress', 'High', maya, 3, ['Code'], 6, [R.combat], 6);
  task('Pathfinding for large units', 'In progress', 'Urgent', maya, -1, ['Code'], 12, [R.magmaGolem, R.ancientOak], 10);
  task('Balance pass: Frostbound units', 'To do', 'High', ana, 7, ['Design'], 4, [R.frostSentinel, R.rimeblade, R.glacierMystic], 4);
  task('Portrait art: Rimeblade', 'Review', 'Medium', kenji, 1, ['Art'], 8, [R.rimeblade, portraits.rimeblade], 7);
  task('Portrait art: Ancient Oak', 'In progress', 'Medium', kenji, 5, ['Art'], 8, [R.ancientOak, portraits.ancientOak], 3);
  task('SFX: Fireball impact crackle layer', 'To do', 'Low', kenji, 10, ['Audio'], 2, [R.fireball, fireballSfx], 2);
  task('Heat meter UI implementation', 'To do', 'Medium', maya, 9, ['Code', 'Design'], 5, [R.heat, heatMeter], 3);
  task('Playtest #3 — survey & notes', 'Backlog', 'High', ana, 2, ['QA'], 3, [R.combat, R.heat], 2);
  task('Prototype fog of war shader', 'Backlog', 'Medium', maya, null, ['Code'], 10, [R.fog], 5);
  task('Particle pass: Frost Nova', 'Backlog', 'Low', kenji, null, ['Art'], 4, [R.frostNova], 5);
  task('Frostbound color script', 'Review', 'Medium', kenji, 2, ['Art'], 3, [frostPalette], 6);
  task('Write lore: The Sundering', 'Done', 'Low', kenji, -8, ['Writing'], 3, [R.sundering], 30);
  task('Upkeep tooltip copy', 'Done', 'Low', ana, -3, ['Writing'], 1, [R.economy], 12);

  /* ---------------------------------------------------------------- polls */
  const artPoll = p.createPoll(
    {
      question: 'Art direction for the Frostbound',
      description: 'Kenji turns the winner into a style sheet by Friday.',
      mode: 'single',
      options: [{ label: 'Nordic runes & carved ice' }, { label: 'Crystalline elegance' }, { label: 'Frozen undead' }],
      closesAt: t0 + 3 * DAY,
      linkedRecordId: frostPalette,
    },
    kenji.id,
    t0 - 2 * DAY,
  );
  const art = p.getPoll(artPoll)!;
  p.putPollVote(artPoll, ana.id, { choices: [art.options[1]!.id], at: t0 - DAY });
  p.putPollVote(artPoll, kenji.id, { choices: [art.options[0]!.id], at: t0 - DAY });
  p.putPollVote(artPoll, maya.id, { choices: [art.options[1]!.id], at: t0 - DAY / 2 });

  const cutPoll = p.createPoll(
    {
      question: 'Which units should we cut from the demo?',
      description: 'Pick any number. We need the roster down to 8 for the vertical slice.',
      mode: 'multiple',
      options: [
        { label: 'Pyromancer', recordId: R.pyromancer },
        { label: 'Grove Tender', recordId: R.groveTender },
        { label: 'Glacier Mystic', recordId: R.glacierMystic },
        { label: 'Thornback', recordId: R.thornback },
      ],
    },
    ana.id,
    t0 - DAY,
  );
  const cut = p.getPoll(cutPoll)!;
  p.putPollVote(cutPoll, ana.id, { choices: [cut.options[1]!.id], at: t0 - DAY / 2 });
  p.putPollVote(cutPoll, maya.id, { choices: [cut.options[1]!.id, cut.options[2]!.id], at: t0 - DAY / 3 });

  const themePoll = p.createPoll(
    {
      question: 'Rate the Season 2 map themes',
      description: '1 = not excited, 5 = can’t wait to build it.',
      mode: 'score',
      options: [{ label: 'Volcanic archipelago' }, { label: 'Sunken frost citadel' }, { label: 'Overgrown ruins' }],
    },
    maya.id,
    t0 - 3 * DAY,
  );
  const theme = p.getPoll(themePoll)!;
  const [o1, o2, o3] = theme.options.map((o) => o.id) as [string, string, string];
  p.putPollVote(themePoll, ana.id, { scores: { [o1]: 5, [o2]: 3, [o3]: 4 }, at: t0 - 2 * DAY });
  p.putPollVote(themePoll, kenji.id, { scores: { [o1]: 4, [o2]: 5, [o3]: 3 }, at: t0 - 2 * DAY });
  p.putPollVote(themePoll, maya.id, { scores: { [o1]: 5, [o2]: 4, [o3]: 2 }, at: t0 - DAY });

  const permaPoll = p.createPoll(
    {
      question: 'Should heroes permanently die in the campaign?',
      mode: 'single',
      options: [{ label: 'Yes, always' }, { label: 'No' }, { label: 'Only on Hard' }],
    },
    ana.id,
    t0 - 15 * DAY,
  );
  const perma = p.getPoll(permaPoll)!;
  p.putPollVote(permaPoll, ana.id, { choices: [perma.options[2]!.id], at: t0 - 14 * DAY });
  p.putPollVote(permaPoll, kenji.id, { choices: [perma.options[0]!.id], at: t0 - 14 * DAY });
  p.putPollVote(permaPoll, maya.id, { choices: [perma.options[2]!.id], at: t0 - 13 * DAY });
  p.closePoll(permaPoll, perma.options[2]!.id, 'Keeps Normal accessible; Hard players asked for real stakes.');

  /* ---------------------------------------------------------------- board */
  const board = p.createBoard({ name: 'Season 2 brainstorm', description: 'What should the first big update be about?' });
  const note = (x: number, y: number, color: ColorName, text: string, author: Person, votes: Person[] = []) => ({
    x,
    y,
    color,
    text,
    createdBy: author.id,
    votes: Object.fromEntries(votes.map((v) => [v.id, 1])),
  });
  p.addBoardItems(board, [
    { type: 'text', x: 40, y: 0, w: 300, h: 48, text: 'Maps', color: 'gray', createdBy: ana.id },
    note(40, 70, 'yellow', 'Volcanic archipelago with naval lanes', maya, [ana, maya]),
    note(260, 70, 'yellow', 'Frozen citadel siege map', kenji, [kenji]),
    note(40, 260, 'yellow', 'Shifting sands: terrain changes every 5 minutes', ana),
    { type: 'text', x: 540, y: 0, w: 300, h: 48, text: 'Units', color: 'gray', createdBy: ana.id },
    note(540, 70, 'blue', 'Frost wyrm — first air unit', kenji, [kenji, maya, ana]),
    note(760, 70, 'blue', 'Ember siege engineers', ana),
    note(540, 260, 'green', 'Druid shapeshifters for the Verdant Circle', maya, [maya]),
    { type: 'text', x: 1040, y: 0, w: 300, h: 48, text: 'Modes', color: 'gray', createdBy: ana.id },
    note(1040, 70, 'pink', 'King of the hill', maya),
    note(1260, 70, 'pink', '3v3 ranked', ana, [ana]),
    note(1040, 260, 'pink', 'Weekly mutators (double Heat, no fog…)', kenji, [kenji, ana]),
    note(1260, 260, 'purple', 'Season pass with cosmetic-only rewards', maya),
  ]);
}
